import React from "react";
import PropTypes from "prop-types";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Popover from "@material-ui/core/Popover";
import Box from "@material-ui/core/Box";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import InfoOutlined from "@material-ui/icons/InfoOutlined";

/**
 * Estilos locales del botón/overlay informativo.
 *
 * Criterio UX:
 * - escritorio: tooltip clásico por hover
 * - tablet/iPad táctil: tooltip con aspecto de escritorio, pero abierto por toque
 * - móvil pequeño: popover anclado al icono
 *
 * Objetivos:
 * - evitar que el bocadillo tape elementos importantes en móvil
 * - mantener en iPad/tablet el mismo look que en desktop
 * - limitar el ancho del contenido y respetar viewport
 * - mover la flecha del bocadillo arriba a la izquierda en móvil
 */
const useStyles = makeStyles((theme) => ({
  mobilePopoverPaper: {
    position: "relative",
    marginTop: theme.spacing(1),
    width: "min(320px, calc(100vw - 16px))",
    maxWidth: "calc(100vw - 16px)",
    borderRadius: 10,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.18)",
    overflow: "visible",

    /**
     * Flecha decorativa del bocadillo.
     * En móvil la colocamos arriba a la izquierda.
     */
    "&::before": {
      content: '""',
      position: "absolute",
      top: -8,
      left: 18,
      width: 16,
      height: 16,
      backgroundColor: "#616161",
      transform: "rotate(45deg)",
      borderRadius: 2,
      zIndex: -1,
    },
  },

  mobilePopoverBody: {
    backgroundColor: "#616161",
    color: "#fff",
    padding: theme.spacing(1.25, 1.5),
    fontSize: "0.88rem",
    lineHeight: 1.55,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    whiteSpace: "normal",
  },

  tooltipWrapper: {
    display: "inline-flex",
  },
}));

/**
 * Botón de ayuda reutilizable.
 *
 * Comportamiento:
 * - Escritorio real: tooltip clásico.
 * - Tablet/iPad táctil: mismo look de tooltip, pero controlado por tap.
 * - Móvil pequeño: popover controlado por tap/click.
 */
const InfoTooltipButton = ({
  title,
  placement = "right",
  tooltipClasses,
  iconButtonClassName,
  iconClassName,
  ariaLabel = "Información",
}) => {
  const classes = useStyles();
  const theme = useTheme();

  /**
   * Modo móvil pequeño.
   * Esto sí debe activar el popover especial.
   */
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("xs"), {
    noSsr: true,
  });

  /**
   * Detectamos entrada táctil real.
   * En iPad/tablet esto suele ser true.
   */
  const isTouchInput = useMediaQuery("(hover: none) and (pointer: coarse)", {
    noSsr: true,
  });

  /**
   * Tablet táctil:
   * no queremos el popover móvil,
   * pero tampoco podemos depender de hover como en desktop.
   */
  const isTouchTablet = !isSmallMobile && isTouchInput;

  /**
   * Estado para móvil pequeño.
   */
  const [mobileAnchorEl, setMobileAnchorEl] = React.useState(null);

  /**
   * Estado para tablet táctil.
   */
  const [tabletTooltipOpen, setTabletTooltipOpen] = React.useState(false);

  React.useEffect(() => {
    setMobileAnchorEl(null);
    setTabletTooltipOpen(false);
  }, [isSmallMobile, isTouchTablet]);

  if (!title) {
    return null;
  }

  const mobileOpen = Boolean(mobileAnchorEl);

  const handleToggleMobileInfo = (event) => {
    if (!isSmallMobile) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    setMobileAnchorEl((previousAnchor) =>
      previousAnchor ? null : event.currentTarget
    );
  };

  const handleCloseMobileInfo = () => {
    setMobileAnchorEl(null);
  };

  const handleToggleTabletTooltip = (event) => {
    if (!isTouchTablet) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    setTabletTooltipOpen((previousOpen) => !previousOpen);
  };

  const handleCloseTabletTooltip = () => {
    setTabletTooltipOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      handleCloseMobileInfo();
      handleCloseTabletTooltip();
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      if (isSmallMobile) {
        setMobileAnchorEl((previousAnchor) =>
          previousAnchor ? null : event.currentTarget
        );
        return;
      }

      if (isTouchTablet) {
        setTabletTooltipOpen((previousOpen) => !previousOpen);
      }
    }
  };

  /**
   * Desktop real:
   * tooltip clásico por hover/focus.
   */
  if (!isSmallMobile && !isTouchTablet) {
    return (
      <Tooltip
        title={title}
        arrow
        placement={placement}
        classes={tooltipClasses}
      >
        <IconButton className={iconButtonClassName} aria-label={ariaLabel}>
          <InfoOutlined className={iconClassName} />
        </IconButton>
      </Tooltip>
    );
  }

  /**
   * Tablet/iPad táctil:
   * mantenemos el look de tooltip de escritorio,
   * pero lo abrimos/cerramos por toque.
   */
  if (isTouchTablet) {
    return (
      <ClickAwayListener onClickAway={handleCloseTabletTooltip}>
        <span className={classes.tooltipWrapper}>
          <Tooltip
            title={title}
            arrow
            placement={placement}
            classes={tooltipClasses}
            open={tabletTooltipOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
          >
            <IconButton
              className={iconButtonClassName}
              aria-label={ariaLabel}
              aria-haspopup="dialog"
              aria-expanded={tabletTooltipOpen ? "true" : "false"}
              onClick={handleToggleTabletTooltip}
              onKeyDown={handleKeyDown}
            >
              <InfoOutlined className={iconClassName} />
            </IconButton>
          </Tooltip>
        </span>
      </ClickAwayListener>
    );
  }

  /**
   * Móvil pequeño:
   * usamos popover para evitar overlays incómodos.
   */
  return (
    <>
      <IconButton
        className={iconButtonClassName}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={mobileOpen ? "true" : "false"}
        onClick={handleToggleMobileInfo}
        onKeyDown={handleKeyDown}
      >
        <InfoOutlined className={iconClassName} />
      </IconButton>

      <Popover
        open={mobileOpen}
        anchorEl={mobileAnchorEl}
        onClose={handleCloseMobileInfo}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.mobilePopoverPaper }}
        disableRestoreFocus
        marginThreshold={8}
      >
        <Box className={classes.mobilePopoverBody}>{title}</Box>
      </Popover>
    </>
  );
};

InfoTooltipButton.propTypes = {
  title: PropTypes.node,
  placement: PropTypes.oneOf([
    "bottom-end",
    "bottom-start",
    "bottom",
    "left-end",
    "left-start",
    "left",
    "right-end",
    "right-start",
    "right",
    "top-end",
    "top-start",
    "top",
  ]),
  tooltipClasses: PropTypes.object,
  iconButtonClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default InfoTooltipButton;